<template>
  <div
    v-if="featureEnabled"
    class="position-roles-resp-tree"
    :class="{
      'position-roles-resp-tree--empty': displayState === 'empty',
      'position-roles-resp-tree--pane': isPaneView,
    }"
    data-testid="position-roles-resp-tree"
  >
    <div v-if="displayState !== 'empty'" class="prr-tree__filters-bar">
      <SearchBar
        class="prr-tree__search-bar"
        type="button"
        :label="chipText.length > 0 ? 'More filters' : 'Filters'"
        :length="chipText.length"
        :chipText="chipText"
        ghostText="Search role or responsibilities by code or name"
        :add="true"
        :btnText="'Attach'"
        :attachOrgObjectSplitButton="{
          enabled: directAttachEnabled,
          showImportJobDescription: directAttachEnabled,
        }"
        :valueSearch="searchText"
        :counterSection="false"
        :filterBtnFitContent="true"
        :removeColumnClass="true"
        @onSearch="onSearchInput"
        @onClick="onSearchBarClick"
        @removeFilter="onRemoveFilter"
      />
      <PositionsViewSelector
        class="prr-tree__view-selector"
        :selection="viewSelectorSelection"
        :hasCategorizedView="false"
        @update:selection="onViewSelectorChange"
      />
    </div>

    <div
      v-if="displayState === 'filled' && isPaneView"
      class="prr-tree__navigator"
      data-testid="prr-tree-pane"
    >
      <DirectPositionRolesAndResponsibilitiesTreeView
        :data="navigatorTreeData"
        :selected-item="selectedNavigatorItem"
        @item-selected="handleNavigatorItemSelected"
        @drilldown="handleNavigatorDrilldown"
        @role-data-updated="handleRoleDataUpdated"
        @responsibility-data-updated="handleResponsibilityDataUpdated"
      />
    </div>

    <div
      v-else-if="displayState === 'filled'"
      class="prr-tree__table"
      data-testid="prr-tree-filled"
    >
      <div class="prr-tree__header">
        <span class="prr-tree__header-cell prr-tree__header-cell--path"
          >Roles and responsibilities</span
        >
        <span class="prr-tree__header-cell prr-tree__header-cell--detach" />
        <span class="prr-tree__header-cell prr-tree__header-cell--status">Status</span>
        <span class="prr-tree__header-cell prr-tree__header-cell--actions" />
      </div>
      <div class="prr-tree__body" role="tree">
        <PositionRolesResponsibilitiesTreeRow
          v-for="(row, index) in visibleRows"
          :key="`${row.type}-${row.id}`"
          :row="row"
          :level="0"
          :sibling-index="index"
          :is-last-sibling="index === visibleRows.length - 1"
          :expanded-ids="expandedIds"
          :highlight-term="searchText"
          :is-impersonating="isImpersonating"
          :redundant-coverage-enabled="directAttachEnabled"
          @toggle-expand="handleToggleExpand"
          @detach="handleDetach"
          @open-chatter="handleOpenChatter"
          @role-drilldown="handleRoleRowDrilldown"
          @row-menu-action="handleRowMenuAction"
          @drag-start="handleDragStart"
          @drag-state-change="handleDragStateChange"
          @drop-row="handleDrop"
        />
      </div>
    </div>

    <div v-else-if="displayState === 'empty'" class="prr-tree__empty" data-testid="prr-tree-empty">
      <div class="prr-tree__empty-graphic" aria-hidden="true">
        <Icon name="search_lg" />
      </div>
      <h3 class="prr-tree__empty-title">No roles or responsibilities attached</h3>
      <p class="prr-tree__empty-body">
        Employees need roles and responsibilities attached to a position before readiness can be
        tracked. Attach manually or import a job description to get started.
      </p>
      <div class="prr-tree__empty-actions">
        <AttachOrgObjectSplitButton
          v-if="directAttachEnabled"
          @attach-role="onAttachRoleClick"
          @attach-responsibility="onAttachResponsibilityClick"
          @import-job-description="onImportClick"
        />
        <StandardButton
          v-else
          size="small"
          type="magenta"
          variant="primary"
          label="Attach"
          @click="onAttachRoleClick"
        />
      </div>
    </div>

    <NoRecordFound
      v-else-if="displayState === 'not-found'"
      class="prr-tree__not-found"
      data-testid="prr-tree-not-found"
      :title="''"
      message="roles or responsibilities"
      btn-title="Role"
      :subtitle="''"
      show-specific-message="No roles or responsibilities found"
      description="No roles or responsibilities match your current filters or search. Try adjusting them, or clear to see everything."
      :should-show-add-action="false"
      :show-clear-action="true"
      clear-label="Clear filters"
      :no-border="true"
      :show-active-counter="false"
      @onClear="handleClearFilters"
    />

    <FilterRolesResponsibilitiesModal
      :value="filtersModalOpen"
      :filters="filters"
      @input="filtersModalOpen = $event"
      @apply="handleApplyFilters"
      @reset="handleResetFilters"
    />

    <IqSuggestOverlay
      v-if="iqSuggestOpen"
      :value="iqSuggestOpen"
      :suggestions="suggestions"
      :position-name="positionName"
      @input="iqSuggestOpen = $event"
      @accept="handleSuggestionAccept"
      @exit-attempt="handleSuggestExitAttempt"
      @exit-confirmed="handleSuggestExitConfirmed"
    />

    <ImpersonationBlockedModal
      :value="impersonationBlockedOpen"
      :attempted-action="attemptedAction"
      @input="impersonationBlockedOpen = $event"
    />

    <DropToAttachResponsibilityToRoleDialog
      :value="showDropToConvertDialog"
      :responsibility-name="pendingConvert?.source?.name || ''"
      :role-name="pendingConvert?.targetRow?.name || ''"
      :title="'Attach Responsibility to Role'"
      :intro-line="'You are about to attach responsibility'"
      :middle-line="convertDialogMiddleLine"
      :button-label="'Attach'"
      :cancel-label="'Cancel'"
      :error="convertError"
      @input="onConvertDialogInput"
      @attach="handleConfirmConvert"
    />

    <ExActionsPopUp
      :value="showDedupeDialog"
      headline="Detach Duplicate Responsibility"
      :html-info="dedupeMessageHtml"
      action-label="Detach"
      cancel-label="Cancel"
      icon="org_icon_responsibility"
      :apply-border-class="true"
      :center-headline="true"
      :destructive="true"
      :primary="true"
      :width="612"
      actions-top-spacing="32px"
      @input="onDedupeDialogInput"
      @onAction="handleConfirmDedupe"
    />

    <!-- Vue 2 has no Teleport. Host stays in-tree; the portal is moved to document.body
         while a drag is active so overflow:hidden ancestors cannot clip the hand cursor. -->
    <div ref="dragCursorHost" class="prr-drag-cursor-host" aria-hidden="true">
      <div v-if="dragActive" ref="dragCursorPortal" class="prr-drag-cursor-portal">
        <div class="prr-drag-cursor-overlay" />
        <div
          class="prr-drag-cursor"
          :style="{ left: dragCursorX - 12 + 'px', top: dragCursorY - 12 + 'px' }"
        >
          <DragAndDropInPositionsTreeNavigatorCursor
            :action="dragCursorAction"
            :state="dragCursorUiState"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  ref,
  watch,
} from "vue";
import Icon from "@components/icons/Icon.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import PositionsViewSelector from "@components/PositionsViewSelector/PositionsViewSelector.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import AttachOrgObjectSplitButton from "@components/AttachOrgObjectSplitButton/AttachOrgObjectSplitButton.vue";
import DragAndDropInPositionsTreeNavigatorCursor from "@components/DragAndDropInPositionsTreeNavigatorCursor/DragAndDropInPositionsTreeNavigatorCursor.vue";
import DropToAttachResponsibilityToRoleDialog from "@components/RolesCatalog/DropToAttachResponsibilityToRoleDialog/DropToAttachResponsibilityToRoleDialog.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import FilterRolesResponsibilitiesModal from "./FilterRolesResponsibilitiesModal.vue";
import IqSuggestOverlay from "./IqSuggestOverlay.vue";
import ImpersonationBlockedModal from "./ImpersonationBlockedModal.vue";
import PositionRolesResponsibilitiesTreeRow from "./PositionRolesAndResponsibilitiesTreeRow.vue";
import DirectPositionRolesAndResponsibilitiesTreeView from "@components/DirectPositionRolesAndResponsibilitiesTreeView/DirectPositionRolesAndResponsibilitiesTreeView.vue";
import {
  LD_FLAG_POSITION_ROLES_RESPONSIBILITIES_TREE,
  LD_FLAG_IDEA_MDP_585,
} from "@components/constants/launchDarklyFlagKeys";
import {
  convertResponsibilityToRole,
  detachResponsibilityFromPosition,
} from "@/api/responsibilityAPI";
import { attachResponsibilityToRole } from "@/api/roleAPI";

const props = defineProps({
  state: {
    type: String,
    default: "filled",
    validator: (value) => ["filled", "empty", "not-found"].includes(value),
  },
  rows: {
    type: Array,
    default: () => [],
  },
  filters: {
    type: Object,
    default: () => ({ status: [], archived: "Do Not Show", types: [], search: "" }),
  },
  view: {
    type: String,
    default: "tree",
    validator: (value) => ["tree", "list"].includes(value),
  },
  isImpersonating: {
    type: Boolean,
    default: false,
  },
  suggestions: {
    type: Array,
    default: () => [],
  },
  positionName: {
    type: String,
    default: "",
  },
  accountId: {
    type: [Number, String],
    default: "",
  },
});

const emit = defineEmits([
  "toggle-expand",
  "detach",
  "attach",
  "attach-role",
  "attach-responsibility",
  "suggest",
  "suggest-exit-attempt",
  "import-job-description",
  "filters-change",
  "open-filters-modal",
  "apply-filters",
  "reset-filters",
  "clear-filters",
  "open-chatter",
  "row-menu-action",
  "view-change",
  "item-selected",
  "drilldown",
  "role-drilldown",
  "impersonation-blocked",
  "converted",
  "reorder",
  "role-data-updated",
  "responsibility-data-updated",
]);

// Captured once at setup top-level: getCurrentInstance() is unavailable inside
// plain event handlers, so handlers below use this proxy for $getFlag/$loader/...
const proxy = getCurrentInstance()?.proxy;

const featureEnabled = ref(true);
const directAttachEnabled = ref(false);
const currentView = ref(props.view);
const searchText = ref(props.filters.search || "");
const chipText = ref([]);
const expandedIds = ref([]);
// ER-11547 — ids that were expanded automatically because a search term matched
// something nested inside them. Tracked separately from `expandedIds` so clearing
// the search can collapse exactly the rows the search opened, while leaving any row
// the user expanded by hand untouched.
const searchExpandedIds = ref([]);
const filtersModalOpen = ref(false);
const iqSuggestOpen = ref(false);
const acceptedSuggestions = ref([]);
const impersonationBlockedOpen = ref(false);
const attemptedAction = ref("");

const dragActive = ref(false);
const dragCursorX = ref(0);
const dragCursorY = ref(0);
const dragCursorUiState = ref("Dragging");
const dragCursorAction = ref("Attach");
const dragCursorHost = ref(null);
const dragCursorPortal = ref(null);
const showDropToConvertDialog = ref(false);
const pendingConvert = ref(null);
const convertError = ref("");
const showDedupeDialog = ref(false);
const pendingDedupe = ref(null);
const dedupeError = ref("");
const convertDialogMiddleLine = computed(() =>
  pendingConvert.value?.kind === "attach"
    ? "to role"
    : "to role, detaching it directly from the Position"
);

function escapeHtml(s) {
  if (s == null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Rule 18a — confirm dialog copy is rendered verbatim from the concept page, with
// the role name interpolated and escaped before it reaches the v-html bound info.
// Markup carries no inline style attributes: the dialog already styles the body via
// its own `action_popup_message` / `message-spaced` classes (block, centred, 20px
// line-height), the role name uses semantic <strong>, and the error uses the shared
// Vuetify `error--text` utility class (PR checklist §10).
const dedupeMessageHtml = computed(() => {
  const roleName = escapeHtml(pendingDedupe.value?.targetRow?.name || "");
  const inner = [
    "This responsibility is already attached to Role ",
    `<strong>${roleName}</strong>`,
    ". Detach the duplicate direct attachment from this Position?",
  ];
  if (dedupeError.value) {
    inner.push(
      "<br /><br />",
      `<span role="alert" class="error--text">${escapeHtml(dedupeError.value)}</span>`
    );
  }
  return inner.join("");
});

// Shared with every nested row so drop-target state can be computed during
// dragover. Browsers refuse dataTransfer.getData() until the drop event.
const dragSession = reactive({ source: null });
provide("prrDragSession", dragSession);

const transparentCursor =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E\") 0 0, none";

let dragCursorRafId = 0;
let lastDragPointerEvent = null;

function restoreDragCursorToHost() {
  const el = dragCursorPortal.value;
  const host = dragCursorHost.value;
  if (el && host && el.parentNode !== host) {
    host.appendChild(el);
  }
}

function mountDragCursorOnBody() {
  nextTick(() => {
    const el = dragCursorPortal.value;
    if (el && el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
  });
}

onBeforeUnmount(() => {
  if (dragActive.value) endDragSession();
  restoreDragCursorToHost();
});

function collectGroupIds(rows, acc = []) {
  (rows || []).forEach((row) => {
    if (row?.type === "group") acc.push(row.id);
    if (row?.children?.length) collectGroupIds(row.children, acc);
  });
  return acc;
}

function chipsFromFilters(filters) {
  const chips = [];
  (filters?.status || []).forEach((status) => chips.push(status));
  if (filters?.archived === "Show") chips.push("Show");
  (filters?.types || []).forEach((type) => chips.push(type));
  return chips;
}

watch(
  () => props.view,
  (value) => {
    currentView.value = value;
  }
);

watch(
  () => props.filters,
  (value) => {
    searchText.value = value?.search || "";
    chipText.value = chipsFromFilters(value);
  },
  { immediate: true, deep: true }
);

watch(
  () => props.rows,
  (rows) => {
    const groupIds = collectGroupIds(rows);
    if (!groupIds.length) return;
    const next = new Set(expandedIds.value);
    groupIds.forEach((id) => next.add(id));
    expandedIds.value = [...next];
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    featureEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_POSITION_ROLES_RESPONSIBILITIES_TREE, true)) !== false;
    directAttachEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_585, false)) === true;
  } catch {
    featureEnabled.value = true;
    directAttachEnabled.value = false;
  }
});

const displayState = computed(() => {
  const hasAccepted = acceptedSuggestions.value.length > 0;
  const hasVisible = visibleRows.value.length > 0;
  const hasSource = (props.rows || []).length > 0 || hasAccepted;

  if (hasAccepted && hasVisible) return "filled";
  if (hasVisible && props.state !== "not-found") return "filled";
  if (!hasSource && props.state !== "not-found") return "empty";
  return "not-found";
});

const viewSelectorSelection = computed(() => (currentView.value === "list" ? "Pane" : "Grid"));
const isPaneView = computed(() => currentView.value === "list");

function navigatorDisplayName(row) {
  const name = String(row?.name || "").trim();
  if (name.startsWith("#")) return name;
  const code = String(row?.code || "").trim();
  if (code && name) return `${code} - ${name}`;
  return name || code;
}

function toNavigatorNode(row) {
  if (!row || row.type === "group") return null;
  const children = (row.children || []).map(toNavigatorNode).filter(Boolean);
  // Roles must use the catalog id (roleId). Responsibilities must keep their
  // own id — using roleId here would make every child open the same RoleCard.
  const id = row.type === "role" ? (row.roleId ?? row.id) : row.id;
  return {
    id,
    type: row.type,
    name: navigatorDisplayName(row),
    status: String(row.status || "active").toLowerCase(),
    expanded: false,
    children,
  };
}

const navigatorTreeData = computed(() =>
  flattenGroups(filteredRows.value).map(toNavigatorNode).filter(Boolean)
);

const selectedNavigatorItem = ref(null);

function findNavigatorItem(id, items = navigatorTreeData.value) {
  for (const item of items || []) {
    if (item.id === id || String(item.id) === String(id)) return item;
    const nested = findNavigatorItem(id, item.children || []);
    if (nested) return nested;
  }
  return null;
}

function defaultNavigatorSelection(items = navigatorTreeData.value) {
  if (!items?.length) return null;
  const firstRole = items.find((item) => item.type === "role");
  const next = firstRole || items[0];
  return next ? { id: next.id, type: next.type, name: next.name } : null;
}

function rowMatches(row) {
  // Group headers are containers; they stay visible only when a child matches.
  if (row?.type === "group") return false;

  const statuses = (props.filters.status || []).map((status) => String(status).toLowerCase());
  if (statuses.length && !statuses.includes(String(row.status || "").toLowerCase())) return false;

  if (row.archived && props.filters.archived === "Do Not Show") return false;

  const types = (props.filters.types || []).map((type) => String(type).toLowerCase());
  if (types.length && !types.includes(String(row.type || "").toLowerCase())) return false;

  const search = String(props.filters.search || searchText.value || "")
    .trim()
    .toLowerCase();
  if (search) {
    const haystack = `${row.code || ""} ${row.name || ""} ${row.count || ""}`.toLowerCase();
    if (!haystack.includes(search)) return false;
  }
  return true;
}

function filterTree(rows) {
  return rows.reduce((acc, row) => {
    const children = row.children ? filterTree(row.children) : [];
    if (rowMatches(row) || children.length) {
      acc.push({ ...row, children });
    }
    return acc;
  }, []);
}

const filteredRows = computed(() => filterTree(props.rows || []));

// Figma does not show a "N directly attached responsibilities" group header —
// unwrap those containers so their children render as top-level rows.
function flattenGroups(rows) {
  return (rows || []).reduce((acc, row) => {
    if (row?.type === "group") {
      acc.push(...flattenGroups(row.children || []));
      return acc;
    }
    acc.push(row);
    return acc;
  }, []);
}

watch(
  navigatorTreeData,
  (items) => {
    if (!items?.length) {
      selectedNavigatorItem.value = null;
      return;
    }
    const currentId = selectedNavigatorItem.value?.id;
    if (currentId != null && findNavigatorItem(currentId, items)) return;
    selectedNavigatorItem.value = defaultNavigatorSelection(items);
  },
  { immediate: true }
);

const visibleRows = computed(() => {
  const accepted = acceptedSuggestions.value;
  const base = flattenGroups(filteredRows.value);
  if (!accepted.length) return base;
  return [
    ...base,
    ...accepted.map((suggestion) => ({ ...suggestion, children: suggestion.children || [] })),
  ];
});

// ER-11547 — the effective search term. `rowMatches()`/`filterTree()` already fall
// back to the local `searchText` ref so the tree reacts while the user is still typing
// (before the parent round-trips the updated `filters` prop back down).
const activeSearch = computed(() =>
  String(props.filters?.search || searchText.value || "").trim()
);

// Every non-group row in the (already filtered) tree that still has children is an
// ancestor of a match — expanding it is what makes the match visible.
function collectExpandableIds(rows, acc = []) {
  (rows || []).forEach((row) => {
    if (row?.type !== "group" && Array.isArray(row?.children) && row.children.length > 0) {
      acc.push(row.id);
    }
    if (row?.children?.length) collectExpandableIds(row.children, acc);
  });
  return acc;
}

// Expand the ancestors of every search match without disturbing manual expansions.
function autoExpandForSearch(rows) {
  const ids = collectExpandableIds(rows);
  if (!ids.length) return;
  const next = new Set(expandedIds.value);
  const auto = new Set(searchExpandedIds.value);
  ids.forEach((id) => {
    if (!next.has(id)) {
      next.add(id);
      auto.add(id);
    }
  });
  expandedIds.value = [...next];
  searchExpandedIds.value = [...auto];
}

// Collapse the rows the search opened (but keep any the user then toggled by hand —
// toggling removes the id from `searchExpandedIds`).
function clearSearchExpansions() {
  if (!searchExpandedIds.value.length) return;
  const opened = new Set(searchExpandedIds.value);
  expandedIds.value = expandedIds.value.filter((id) => !opened.has(id));
  searchExpandedIds.value = [];
}

watch(
  [activeSearch, filteredRows],
  ([search]) => {
    if (search) {
      autoExpandForSearch(filteredRows.value);
    } else {
      clearSearchExpansions();
    }
  },
  { immediate: true }
);

function handleToggleExpand({ rowId, expanded }) {
  // A manual toggle takes ownership of the row: drop it from the search-opened set
  // so clearing the search no longer affects it.
  if (searchExpandedIds.value.includes(rowId)) {
    searchExpandedIds.value = searchExpandedIds.value.filter((id) => id !== rowId);
  }
  const next = new Set(expandedIds.value);
  if (expanded) next.add(rowId);
  else next.delete(rowId);
  expandedIds.value = [...next];
  emit("toggle-expand", { rowId, expanded });
}

function onRemoveFilter(chip) {
  const label = String(chip || "");
  const next = { ...props.filters };
  const statuses = [...(next.status || [])];
  const types = [...(next.types || [])];
  if (statuses.includes(label)) {
    next.status = statuses.filter((item) => item !== label);
  } else if (types.includes(label)) {
    next.types = types.filter((item) => item !== label);
  } else if (label === "Show" || label === "Show Archived") {
    next.archived = "Do Not Show";
  }
  emit("filters-change", { filters: next });
}

function onSearchInput(value) {
  const text = typeof value === "string" ? value : searchText.value;
  searchText.value = text;
  emit("filters-change", { filters: { ...props.filters, search: text } });
}

function onSearchBarClick(type) {
  if (type === "add" || type === "attach-role") {
    onAttachRoleClick();
    return;
  }
  if (type === "attach-responsibility") {
    onAttachResponsibilityClick();
    return;
  }
  if (type === "import-job-description") {
    onImportClick();
    return;
  }
  openFiltersModal();
}

function openFiltersModal() {
  emit("open-filters-modal");
  filtersModalOpen.value = true;
}

function handleApplyFilters(filters) {
  filtersModalOpen.value = false;
  emit("apply-filters", { filters });
}

function handleResetFilters() {
  emit("reset-filters");
}

function handleClearFilters() {
  searchText.value = "";
  const next = {
    status: ["Active", "Draft"],
    archived: "Do Not Show",
    types: [],
    search: "",
  };
  emit("clear-filters");
  emit("filters-change", { filters: next });
}

function block(action) {
  attemptedAction.value = action;
  impersonationBlockedOpen.value = true;
  emit("impersonation-blocked", { attemptedAction: action });
}

function onAttachClick() {
  onAttachRoleClick();
}

function onAttachRoleClick() {
  if (props.isImpersonating) return block("attach");
  emit("attach");
  emit("attach-role");
}

function onAttachResponsibilityClick() {
  if (!directAttachEnabled.value) return;
  if (props.isImpersonating) return block("attach");
  emit("attach-responsibility");
}

// Suggest entry point hidden until the flow is implemented (Direct Responsibility Attachment Rule 21).
function onSuggestClick() {
  if (props.isImpersonating) return block("suggest");
  emit("suggest");
  iqSuggestOpen.value = true;
}

function onImportClick() {
  if (!directAttachEnabled.value) return;
  if (props.isImpersonating) return block("import-job-description");
  emit("import-job-description");
}

function handleDetach({ rowId, parentId, rowType }) {
  if (props.isImpersonating) return block("detach");
  emit("detach", { rowId, parentId, rowType });
}

const ROW_MENU_ACTION_LABELS = {
  activate_role: "Activate",
  activate_responsibility: "Activate",
  retired_role: "Retire",
  retire_responsibility: "Retire",
  re_activate_role: "Re-activate",
  re_activate_responsibility: "Re-activate",
  attach_responsibility_role: "Attach Responsibility",
  rename_role: "Rename",
  rename_responsibility: "Rename",
  delete_role: "Delete",
  delete_responsibility: "Delete",
  archive_action: "Archive",
  unarchive_action: "Unarchive",
};

function handleRowMenuAction(payload) {
  if (props.isImpersonating) {
    return block(ROW_MENU_ACTION_LABELS[payload?.value] || "row action");
  }
  emit("row-menu-action", payload);
}

function handleDrop({ source, targetRow, kind, position }) {
  if (kind === "reorder-resp" || kind === "reorder-role") {
    if (props.isImpersonating) return block("reorder");
    emit("reorder", { source, targetRow, kind, position: position || "before" });
    return;
  }
  if (kind === "dedupe") {
    if (props.isImpersonating) return block("detach");
    pendingDedupe.value = { source, targetRow };
    dedupeError.value = "";
    showDedupeDialog.value = true;
    return;
  }
  if (props.isImpersonating) return block(kind === "attach" ? "attach" : "convert");
  pendingConvert.value = { source, targetRow, kind };
  convertError.value = "";
  showDropToConvertDialog.value = true;
}

function onConvertDialogInput(value) {
  showDropToConvertDialog.value = value;
  if (!value) {
    pendingConvert.value = null;
    convertError.value = "";
  }
}

function extractApiError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.detail) return String(data.detail);
  if (Array.isArray(data?.non_field_errors)) return data.non_field_errors.join(", ");
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    if (Array.isArray(first)) return first.join(", ");
    if (typeof first === "string") return first;
  }
  return (
    error?.message ||
    fallback ||
    "Could not convert the responsibility to a role attachment. Please try again."
  );
}

async function handleConfirmConvert() {
  const pending = pendingConvert.value;
  if (!pending) return;
  if (!props.accountId) {
    convertError.value = "Account is required to convert this responsibility.";
    return;
  }
  convertError.value = "";
  proxy?.$loader?.show?.();
  try {
    if (pending.kind === "attach") {
      await attachResponsibilityToRole(props.accountId, pending.targetRow.roleId, {
        attachment_id: pending.source.rowId,
      });
    } else {
      await convertResponsibilityToRole(
        props.accountId,
        pending.source.attachmentId,
        pending.targetRow.roleId
      );
    }
    const respPart = pending.source.name || "Responsibility";
    const rolePart = pending.targetRow.name || "role";
    proxy?.$notifications?.success?.(
      "Responsibility successfully attached",
      `Responsibility ${respPart} was attached to role ${rolePart}`,
      5000
    );
    emit("converted", {
      responsibilityRowId: pending.source.rowId,
      attachmentId: pending.source.attachmentId,
      roleId: pending.targetRow.roleId,
    });
    showDropToConvertDialog.value = false;
    pendingConvert.value = null;
  } catch (error) {
    console.error("Error converting responsibility to role:", error);
    convertError.value = extractApiError(error);
  } finally {
    proxy?.$loader?.hide?.();
  }
}

function onDedupeDialogInput(value) {
  showDedupeDialog.value = value;
  if (!value) {
    pendingDedupe.value = null;
    dedupeError.value = "";
  }
}

async function handleConfirmDedupe() {
  const pending = pendingDedupe.value;
  if (!pending) return;
  if (!props.accountId) {
    dedupeError.value = "Account is required to detach this responsibility.";
    return;
  }
  dedupeError.value = "";
  proxy?.$loader?.show?.();
  try {
    // Rule 18a — detach the direct attachment only; the Role already carries the
    // Responsibility, so no Role-attach step runs (unlike the Rule 18 convert path).
    await detachResponsibilityFromPosition(props.accountId, pending.source.attachmentId, {
      reason: "SUPERSEDED_BY_ROLE",
      role_id: pending.targetRow.roleId,
      role_name: pending.targetRow.name,
    });
    const respPart = pending.source.name || "Responsibility";
    const rolePart = pending.targetRow.name || "role";
    proxy?.$notifications?.success?.(
      "Duplicate responsibility detached",
      `Responsibility ${respPart} was detached from this Position (now covered by Role ${rolePart})`,
      5000
    );
    emit("converted", {
      responsibilityRowId: pending.source.rowId,
      attachmentId: pending.source.attachmentId,
      roleId: pending.targetRow.roleId,
    });
    showDedupeDialog.value = false;
    pendingDedupe.value = null;
  } catch (error) {
    console.error("Error detaching duplicate responsibility:", error);
    dedupeError.value = extractApiError(
      error,
      "Could not detach the duplicate responsibility. Please try again."
    );
  } finally {
    proxy?.$loader?.hide?.();
  }
}

function handleDragStart(payload) {
  dragSession.source = {
    rowId: payload?.rowId,
    rowType: payload?.rowType,
    parentType: payload?.parentType || null,
    parentId: payload?.parentId ?? null,
    attachmentId: payload?.attachmentId,
    name: payload?.name,
  };
  dragCursorX.value = payload?.clientX ?? 0;
  dragCursorY.value = payload?.clientY ?? 0;
  dragCursorUiState.value = "Dragging";
  dragCursorAction.value =
    payload?.rowType === "role" ||
    (payload?.rowType === "responsibility" && payload?.parentType === "role")
      ? "Reorder"
      : "Attach";
  dragActive.value = true;
  mountDragCursorOnBody();
  document.documentElement.classList.add("checkpoint-drag-cursor-active");
  document.body.classList.add("checkpoint-drag-cursor-active");
  document.documentElement.style.setProperty("cursor", transparentCursor, "important");
  document.body.style.setProperty("cursor", transparentCursor, "important");
  // `mousemove` is not fired while a native HTML5 drag is in progress — `dragover`
  // is the event that actually tracks the cursor during the drag.
  document.addEventListener("dragover", onDragMove);
  document.addEventListener("dragend", endDragSession);
  document.addEventListener("drop", endDragSession);
}

function handleDragStateChange({ allowed, action }) {
  // HTML5 fires dragleave on the previous row AFTER dragenter on the next, so
  // treating `null` as Dragging would flash the white hand on top of a red/green
  // leaf. Only boolean updates from a live hover set the colored hand; leaving
  // the tree is handled in onDragMove.
  if (action === "Move" || action === "Attach" || action === "Reorder") {
    dragCursorAction.value = action;
  }
  if (allowed === true) {
    dragCursorUiState.value = "Allowed";
  } else if (allowed === false) {
    dragCursorUiState.value = "Blocked";
  }
}

function flushDragCursor() {
  dragCursorRafId = 0;
  const event = lastDragPointerEvent;
  if (!event) return;
  dragCursorX.value = event.clientX;
  dragCursorY.value = event.clientY;
}

function pointerOverDropTarget(event) {
  let target = event.target;
  if (!target) return null;
  if (target.nodeType !== 1) target = target.parentElement;
  if (!target || typeof target.closest !== "function") return null;
  return target.closest(
    "[data-testid='prr-tree-row-drop-target'], [data-testid='prr-tree-row-reorder-gap-before'], [data-testid='prr-tree-row-reorder-gap-after']"
  );
}

function onDragMove(event) {
  lastDragPointerEvent = event;
  const over = pointerOverDropTarget(event);
  if (!over || over.classList.contains("prr-tree-row__main--dragging")) {
    if (dragCursorUiState.value !== "Dragging") dragCursorUiState.value = "Dragging";
  }
  if (!dragCursorRafId) {
    dragCursorRafId = requestAnimationFrame(flushDragCursor);
  }
}

function endDragSession() {
  if (dragCursorRafId) {
    cancelAnimationFrame(dragCursorRafId);
    dragCursorRafId = 0;
  }
  lastDragPointerEvent = null;
  restoreDragCursorToHost();
  dragActive.value = false;
  dragCursorUiState.value = "Dragging";
  dragCursorAction.value = "Attach";
  dragSession.source = null;
  document.removeEventListener("dragover", onDragMove);
  document.removeEventListener("dragend", endDragSession);
  document.removeEventListener("drop", endDragSession);
  document.documentElement.classList.remove("checkpoint-drag-cursor-active");
  document.body.classList.remove("checkpoint-drag-cursor-active");
  document.documentElement.style.removeProperty("cursor");
  document.body.style.removeProperty("cursor");
}

function handleOpenChatter({ rowId, rowType }) {
  emit("open-chatter", { rowId, rowType });
}

function handleSuggestionAccept(suggestion) {
  acceptedSuggestions.value = [...acceptedSuggestions.value, suggestion];
}

function handleSuggestExitAttempt(payload) {
  emit("suggest-exit-attempt", payload);
}

function handleSuggestExitConfirmed() {
  iqSuggestOpen.value = false;
}

function setView(view) {
  currentView.value = view;
  emit("view-change", { view });
}

function onViewSelectorChange(selection) {
  setView(selection === "Pane" ? "list" : "tree");
}

function handleNavigatorItemSelected(item) {
  selectedNavigatorItem.value = item
    ? { id: item.id, type: item.type, name: item.name }
    : null;
  emit("item-selected", item);
}

function handleNavigatorDrilldown(data) {
  emit("drilldown", data);
}

function handleRoleRowDrilldown(role) {
  emit("role-drilldown", role);
}

/**
 * ER-11851: forward detail-panel status refreshes from the nested navigator so the
 * parent (PositionAttachedRolesTab) can re-fetch tree rows and un-grey the changed row.
 */
function handleRoleDataUpdated(payload) {
  emit("role-data-updated", payload);
}

function handleResponsibilityDataUpdated(payload) {
  emit("responsibility-data-updated", payload);
}

defineExpose({
  onViewSelectorChange,
  isPaneView,
  navigatorTreeData,
  selectedNavigatorItem,
});
</script>

<style scoped>
.position-roles-resp-tree {
  --prr-col-detach: 214px;
  --prr-col-status: 88px;
  --prr-col-actions: 96px;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-self: stretch;
  background: #ffffff;
  border-radius: 8px;
  /* Visible so the Actions menu can extend below the filters bar.
     overflow:hidden here clipped Import Job Description on short trees. */
  overflow: visible;
  box-sizing: border-box;
}
.position-roles-resp-tree--empty {
  padding-bottom: 35px;
}
.position-roles-resp-tree--pane {
  flex: 0 1 auto;
  min-height: 0;
  height: auto;
  max-height: none;
  overflow: visible;
}
.prr-tree__navigator {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  height: auto;
  overflow: visible;
}
.prr-tree__filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 84px;
  padding: 20px;
  box-sizing: border-box;
  overflow: visible;
  position: relative;
  z-index: 2;
}
.prr-tree__search-bar {
  flex: 1;
  min-width: 0;
  align-items: center !important;
}
.prr-tree__search-bar :deep(.col) {
  padding: 0;
}
.prr-tree__search-bar :deep(.filter-button) {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-height: 44px;
}
.prr-tree__search-bar :deep(.search-button) {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  min-height: 44px;
  overflow: visible;
}
.prr-tree__search-bar :deep(.ex_text_input) {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  width: auto;
  height: 44px;
  overflow: visible;
}
.prr-tree__search-bar :deep(.ex_text_input .main_label) {
  display: none;
}
.prr-tree__search-bar :deep(.ex_text_input .input-search) {
  margin-top: 0 !important;
  width: 100%;
}
.prr-tree__search-bar :deep(.filter-input-search) {
  flex: 1 1 auto;
  min-width: 0;
  width: auto !important;
  max-width: none;
  float: none;
  margin-top: 0 !important;
  height: 44px;
  overflow: visible;
}
.prr-tree__search-bar :deep(.v-input) {
  align-items: center;
  width: 100%;
}
.prr-tree__search-bar :deep(.v-input__control),
.prr-tree__search-bar :deep(.v-input__slot) {
  min-height: 44px !important;
  margin-bottom: 0 !important;
  overflow: visible;
}
.prr-tree__search-bar :deep(.v-input__slot fieldset) {
  overflow: visible;
}
.prr-tree__search-bar :deep(.v-text-field__details) {
  display: none;
}
.prr-tree__search-bar :deep(.attach-org-object-sb) {
  align-self: flex-start;
  overflow: visible;
}
.prr-tree__search-bar :deep(.search-add-btn) {
  flex: 0 0 auto;
  margin-left: 0 !important;
  margin-top: 0 !important;
  height: 44px;
  display: inline-flex;
  align-items: center;
}
.prr-tree__search-bar :deep(.search-add-btn .storybook-button) {
  height: 44px !important;
  min-height: 44px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
}
.prr-tree__view-selector {
  flex-shrink: 0;
  height: 44px;
  align-self: center;
}
.prr-tree__table {
  width: 100%;
  box-sizing: border-box;
}
.prr-tree__header {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) var(--prr-col-detach) var(--prr-col-status) var(
      --prr-col-actions
    );
  align-items: center;
  height: 44px;
  padding: 0;
  background: #f9fafb;
  border-bottom: 1px solid #eaecf0;
}
.prr-tree__header-cell {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}
.prr-tree__header-cell--path {
  padding: 12px 6px;
}
.prr-tree__header-cell--detach {
  min-width: 198px;
  padding: 12px;
}
.prr-tree__header-cell--status {
  padding: 12px 24px 12px 12px;
}
.prr-tree__header-cell--actions {
  min-width: 88px;
  padding: 12px 24px 12px 12px;
}
.prr-tree__body {
  padding: 0;
}
.prr-tree__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 48px 24px 42px;
  text-align: center;
  background-image: url("~@components/assets/circle_center.svg");
  background-position: top center;
  background-repeat: no-repeat;
}
.prr-tree__not-found {
  width: 100%;
}
.prr-tree__empty-graphic {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0 1px 2px 0 #1018280d;
  color: #667085;
}
.prr-tree__empty-title {
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
  color: #101828;
  margin: 0;
}
.prr-tree__empty-body {
  max-width: 352px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #475467;
  margin: 0;
}
.prr-tree__empty-actions {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}
.prr-tree__import-icon {
  color: #ffffff;
}
</style>

<style>
/* During a tree drag the native cursor is hidden (html/body get the
   checkpoint-drag-cursor-active class at runtime) and the only pointer shown
   is DragAndDropInPositionsTreeNavigatorCursor, mirroring the pattern used by
   OrganizationPositionsTab. */
.prr-drag-cursor-host {
  position: absolute;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
}
.prr-drag-cursor-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: none;
  display: block;
  background: transparent;
}
.prr-drag-cursor {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 2147483647;
  pointer-events: none;
  display: block;
  transform: translateZ(0);
}
html.checkpoint-drag-cursor-active,
html.checkpoint-drag-cursor-active *,
body.checkpoint-drag-cursor-active,
body.checkpoint-drag-cursor-active * {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'/%3E")
      0 0,
    none !important;
}
</style>
